import React, { useState, useEffect, useRef, useContext } from "react";
// import { X, SendHorizontal, MessageCircleMore } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext } from "../../../context/AuthContext";
import { getApi } from "../../../services/api";
import { io } from "socket.io-client";
import { BACKEND_BASE_URL } from "../../../utils/constants";
import {
	CloseCircleFilled,
	CloseOutlined,
	MessageFilled,
	SendOutlined,
} from "@ant-design/icons";

const socket = io(BACKEND_BASE_URL, { transports: ["websocket"] });
function ChatButton() {
	const [isOpen, setIsOpen] = useState(false);
	const [message, setMessage] = useState("");
	const [loading, setLoading] = useState(false);
	// const [messages, setMessages] = useState([]);
	const { user } = useContext(AuthContext);
	const [userName, setUserName] = useState(
		user?.fullName || localStorage.getItem("username") || ""
	);
	const [userId, setUserId] = useState(
		() => user?._id || localStorage.getItem("userId") || null
	);
	const [isAskingName, setIsAskingName] = useState(false);
	const [conversations, setConversations] = useState([]);

	const messagesEndRef = useRef(null);

	const scrollToBottom = () => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
		}
	};

	const toggleChat = async () => {
		if (!isOpen && userName) handleLogin();
		setIsOpen(!isOpen);
		scrollToBottom();
	};
	useEffect(() => {
		const fetchMyMessages = async () => {
			try {
				const myMessages = await getApi(`messages/${userId}`);
				console.log("my messages", myMessages);
				setConversations(myMessages);
			} catch (error) {
				console.error(error);
			}
		};
		fetchMyMessages();
	}, [userId]);
	useEffect(() => {
		setUserName(user?.fullName);
		setUserId(user?._id);
	}, [user]);

	useEffect(() => {
		// Handle user registration
		socket.on("userRegistered", ({ id }) => {
			if (!user?._id) {
				localStorage.setItem("userId", id);
				setUserId(id);
			}
		});

		// Receive messages from admin or other users
		socket.on("receiveMessage", ({ message }) => {
			setConversations((prevMessages) => [
				...prevMessages,
				{
					sender: { id: "admin_group", name: "Admin" },
					recipient: { id: userId, name: userName },
					message,
					timestamp: Date.now(),
				},
			]);
		});
		socket.on("connect", () => {
			if (!userName) return;
			socket.emit("registerUser", { userId, name: userName });
		});

		return () => {
			socket.off("userRegistered");
			socket.off("receiveMessage");
			socket.off("connect");
		};
	}, []);
	useEffect(() => {
		if (isOpen) scrollToBottom();
	}, [conversations, isOpen]);

	const handleLogin = () => {
		socket.emit("registerUser", { userId, name: userName }); // Register user (logged-in or anonymous)
	};

	const handleSendMessage = () => {
		if (!message?.trim()) return;
		setLoading(true);

		socket.emit("sendMessage", {
			userId,
			message: message?.trim(),
			userName,
		});
		setConversations((prevChatHistory) => [
			...prevChatHistory,
			{
				sender: { id: userId, name: userName },
				recipient: "Admin",
				message,
				timestamp: Date.now(),
			},
		]);
		setMessage("");
		setLoading(false);
	};

	useEffect(() => {
		localStorage.setItem("username", userName);
	}, [userName]);

	const handleAskName = (e) => {
		e.preventDefault();
		if (userName.trim() === "") return;

		handleLogin();
		setIsAskingName(false);
	};

	const handleOpenChat = () => {
		if (!userName) {
			setIsAskingName(true);
		}

		toggleChat();
	};

	return (
		<div className="fixed lg:bottom-4 lg:right-7 bottom-28 right-1 z-50">
			<div className="relative">
				<button
					onClick={handleOpenChat}
					className={`bg-black text-white p-4 rounded-full shadow-lg hover:bg-gray-950 transition-transform transform duration-500 ${
						isOpen ? "rotate-180" : "rotate-0"
					}`}
				>
					{isOpen ? (
						<CloseCircleFilled className="rotate-180" />
					) : (
						<MessageFilled className="" />
					)}
				</button>
				{/* {conversation?.adminLastMessageStatus === "unread" && (
					<div className="absolute p-2 top-0 right-0 bg-red-500 rounded-full" />
				)} */}
			</div>

			<AnimatePresence>
				{isOpen && (
					<motion.div
						initial={{
							opacity: 0,
							scale: 0,
							originX: 1,
							originY: 1,
						}}
						animate={{
							opacity: 1,
							scale: 1,
							originX: 1,
							originY: 1,
						}}
						exit={{ opacity: 0, scale: 0, originX: 1, originY: 1 }}
						transition={{ duration: 0.4, ease: "easeOut" }}
						className="fixed lg:bottom-20 bottom-0 right-0 md:right-5 w-full md:w-96 bg-white dark:bg-gray-800 transition-colors duration-300 shadow-xl rounded-xl flex flex-col overflow-hidden max-h-[30rem] min-h-40 border border-gray-300 dark:border-gray-700 z-50"
					>
						<div className="flex justify-between bg-gray-100 dark:bg-gray-900 items-center text-black dark:text-white p-4">
							<h2 className="text-base">Contact Chat</h2>
							<button onClick={toggleChat}>
								<CloseOutlined size={24} />
							</button>
						</div>

						<div className="flex-1 px-5 pt-5 overflow-y-scroll no-scrollbar">
							{isAskingName && (
								<div className="mb-4">
									<p className="text-gray-700 dark:text-gray-300 mb-2">
										What should we call you?
									</p>
									<form onSubmit={handleAskName}>
										<input
											type="text"
											className="border p-2 pl-4 rounded-xl w-full dark:bg-backgroundDark text-black dark:text-white focus:outline-none"
											placeholder="Enter your name..."
											value={userName}
											onChange={(e) =>
												setUserName(e.target.value)
											}
										/>
										<button
											type="submit"
											className="bg-black text-white p-2 mt-2 w-full rounded disabled:bg-gray-600"
											disabled={!userName}
										>
											Submit
										</button>
									</form>
								</div>
							)}

							{!isAskingName && conversations.length === 0 && (
								<p className="text-gray-700 dark:text-gray-300 mb-2 py-4 flex text-center justify-center bg-gray-300 dark:bg-gray-600 rounded-lg">
									Hello{" "}
									{user ? user?.fullName : userName || ""}!
									How can we help you?
								</p>
							)}

							{!isAskingName &&
								conversations.map((msg, index) => {
									const isOwnMessage =
										msg.sender.id === userId;

									return (
										<div
											key={index}
											className={`flex flex-col mb-3 ${
												isOwnMessage
													? "items-end"
													: "items-start"
											}`}
										>
											<div className="relative flex flex-row">
												{!isOwnMessage ? (
													<>
														<div className="absolute w-5 h-5 -left-[0.4rem] bottom-0 bg-gray-500 rounded-br-2xl"></div>
														<div className="absolute w-5 h-5 -left-[1.25rem] bottom-0 bg-backgroundColor dark:bg-backgroundDark rounded-br-xl transition-colors duration-300"></div>
													</>
												) : (
													<>
														<div className="absolute w-5 h-5 -right-[0.4rem] bottom-0 bg-backgroundDark rounded-bl-2xl"></div>
														<div className="absolute w-5 h-5 -right-[1.25rem] bottom-0 bg-backgroundColor dark:bg-backgroundDark rounded-bl-xl transition-colors duration-300"></div>
													</>
												)}

												<div
													className={`max-w-xs lg:max-w-md py-2 px-4 ${
														isOwnMessage
															? "bg-black text-white rounded-2xl"
															: "bg-gray-500 dark:bg-gray-500 text-white rounded-2xl"
													}`}
												>
													{msg.message}
												</div>
											</div>

											<div
												className={`flex text-xs text-gray-400 font-medium mt-1 ${
													isOwnMessage
														? "justify-end"
														: "justify-start"
												}`}
											>
												{new Date(
													msg.timestamp
												).toLocaleTimeString()}
											</div>
										</div>
									);
								})}
							<div ref={messagesEndRef} />
						</div>

						{!isAskingName && (
							<div className="p-3">
								<div className="flex items-center relative">
									<form
										className={`transition-all duration-150 ${
											message?.trim() || loading
												? "mr-10"
												: "mr-0"
										} w-full`}
										onSubmit={(e) => {
											e.preventDefault();
											handleSendMessage();
										}}
									>
										<input
											type="text"
											className="border p-2 pl-4 rounded-xl w-full dark:bg-backgroundDark text-black dark:text-white focus:outline-none"
											placeholder="Type here ..."
											value={message}
											onChange={(e) =>
												setMessage(e.target.value)
											}
										/>
									</form>

									{loading ? (
										<div className="absolute right-3 flex items-center">
											Loading ...
										</div>
									) : (
										<button
											onClick={handleSendMessage}
											disabled={!message?.trim()}
											className="absolute right-2 p-1 bg-transparent text-black hover:text-gray-950 disabled:opacity-0 transition-opacity duration-150 flex items-center"
										>
											<SendOutlined size={26} />
										</button>
									)}
								</div>
							</div>
						)}
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}

export default ChatButton;
